import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Box } from "@mui/material";
import { OcDatePicker, OcTimePicker } from "oc/components";
import { getDateFormatByLocale } from "oc/utils";

interface MyTimesheetNewRequestDialogTimeDurationProps {
  formik: any; // Replace 'any' with the actual Formik type if available, e.g., FormikProps<FormValues>
  isMobile: boolean;
}

export const MyTimesheetNewRequestDialogTimeDuration = ({
  formik,
  isMobile,
}: MyTimesheetNewRequestDialogTimeDurationProps) => {
  const { t, i18n } = useTranslation();

  const handleDateTimeChange = (
    type: "startDate" | "endDate" | "startTime" | "endTime",
    value: any,
  ) => {
    if (!value) return;

    let startDate = formik.values.startTime
      ? moment(formik.values.startTime).format("YYYY-MM-DD")
      : "";
    let startTime = formik.values.startTime
      ? moment(formik.values.startTime).format("HH:mm")
      : "";
    let endDate = formik.values.endTime
      ? moment(formik.values.endTime).format("YYYY-MM-DD")
      : startDate;
    let endTime = formik.values.endTime
      ? moment(formik.values.endTime).format("HH:mm")
      : "";

    const isValidTime = (time: string) => moment(time, "HH:mm", true).isValid();

    switch (type) {
      case "startDate":
        if (moment(value, "YYYY-MM-DD", true).isValid()) {
          startDate = moment(value).format("YYYY-MM-DD");
          const dateString = `${startDate} ${startTime}`.trim();
          formik.setFieldValue("startTime", new Date(dateString));
          formik.setFieldError("startTime", "");
        }
        break;
      case "endDate":
        if (moment(value, "YYYY-MM-DD", true).isValid()) {
          endDate = moment(value).format("YYYY-MM-DD");
          if (!formik.values.endTime) {
            endTime = moment().format("HH:mm");
          }
          const dateString = `${endDate} ${endTime}`.trim();
          formik.setFieldValue("endTime", new Date(dateString));
          formik.setFieldError("endTime", "");
        }
        break;
      case "startTime":
        if (isValidTime(value)) {
          startTime = moment(value).format("HH:mm");
          const dateString = `${startDate} ${startTime}`.trim();
          formik.setFieldValue("startTime", new Date(dateString));
          formik.setFieldError("startTime", "");
        }
        break;
      case "endTime":
        if (isValidTime(value)) {
          endTime = moment(value).format("HH:mm");
          const dateString = `${endDate} ${endTime}`.trim();
          formik.setFieldValue("endTime", new Date(dateString));
          formik.setFieldError("endTime", "");
        }
        break;
      default:
        break;
    }
  };

  const getDateValue = (date: string | Date | null | undefined) => {
    if (date) {
      return new Date(date);
    }
    return null;
  };

  return (
    <Box width={isMobile ? "100%" : "360px"}>
      <Box
        display="flex"
        flexDirection="row"
        width={isMobile ? "100%" : "360px"}
      >
        <Box style={{ width: isMobile ? "100%" : "176px" }}>
          <OcDatePicker
            error={formik.touched.startTime && Boolean(formik.errors.startTime)}
            helperText={""}
            id="startDate"
            label={t("OC_START_DATE")}
            onChange={(date) => handleDateTimeChange("startDate", date)}
            value={getDateValue(formik.values.startTime)}
            format={getDateFormatByLocale(i18n.language)}
          />
        </Box>
        <Box width={isMobile ? "16px" : "8px"} />
        <Box style={{ width: isMobile ? "100%" : "176px" }}>
          <OcTimePicker
            error={formik.touched.startTime && Boolean(formik.errors.startTime)}
            label={t("OC_START_TIME")}
            id="startTime"
            value={getDateValue(formik.values.startTime)}
            onChange={(date) => handleDateTimeChange("startTime", date)}
            width={isMobile ? "100%" : "176px"}
          />
        </Box>
      </Box>
      <Box height="32px" />
      <Box
        display="flex"
        flexDirection="row"
        width={isMobile ? "100%" : "360px"}
        justifyContent="space-between"
      >
        <Box style={{ width: isMobile ? "100%" : "176px" }}>
          <OcDatePicker
            error={formik.touched.endTime && Boolean(formik.errors.endTime)}
            helperText={""}
            id="endDate"
            label={t("OC_END_DATE")}
            onChange={(date) => handleDateTimeChange("endDate", date)}
            value={getDateValue(formik.values.endTime)}
            format={getDateFormatByLocale(i18n.language)}
          />
        </Box>
        {isMobile && <Box width={isMobile ? "16px" : "8px"} />}
        <Box style={{ width: isMobile ? "100%" : "176px" }}>
          <OcTimePicker
            error={formik.touched.endTime && Boolean(formik.errors.endTime)}
            label={t("OC_END_TIME")}
            id="endTime"
            value={getDateValue(formik.values.endTime)}
            onChange={(date) => handleDateTimeChange("endTime", date)}
            width={isMobile ? "100%" : "176px"}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default MyTimesheetNewRequestDialogTimeDuration;
